/**
 * One request per key at a time: a second caller that asks while the first
 * is still in flight gets the same promise. The entry clears when it settles
 * (resolve or reject), so the next ask after that is a fresh request.
 *
 * `force` callers must not be handed an older non-forced read (it may predate
 * the action that made them force), so a forced ask only joins a forced
 * request; a non-forced ask joins either.
 */
const inFlight = new Map()

/**
 * @template T
 * @param {string} key
 * @param {() => Promise<T>} request
 * @param {{ force?: boolean }} [options]
 * @returns {{ promise: Promise<T>, shared: boolean }}
 */
export function shareOllieRequest(key, request, { force = false } = {}) {
  const current = inFlight.get(key)
  if (current && (!force || current.force)) {
    return { promise: current.promise, shared: true }
  }

  let started
  try {
    started = Promise.resolve(request())
  } catch (error) {
    started = Promise.reject(error)
  }
  const promise = started.finally(() => {
    if (inFlight.get(key)?.promise === promise) inFlight.delete(key)
  })
  inFlight.set(key, { promise, force: Boolean(force) })
  return { promise, shared: false }
}

export const isOllieRequestInFlight = key => inFlight.has(key)

/** Forget in-flight requests (tests; leaving a venue). */
export const clearOllieInFlight = () => inFlight.clear()
